import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMessage } from '../../contexts/MessageContext'
import { useApi } from '../../utils/api'
import { PageTitleBar } from '../../components/PageTitleBar'
import { FormLabel, FormSelect, FormInput, FormButton, CancelButton } from '../../components/forms/FormElements'

interface FormData {
  status: string;
  adminLevel: string;
  name: string;
  email: string;
  password: string;
}

export default function EditAdminUser() {
  const { id } = useParams<{ id: string }>()
  const { setMessage } = useMessage()
  const { fetchWithAuth } = useApi()
  const [formData, setFormData] = useState<FormData>({
    status: '',
    adminLevel: '',
    name: '',
    email: '',
    password: '',
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchAdminUser = async () => {
      try {
        const response = await fetchWithAuth(`/admin/admin-users/${id}`)
        if (!response.ok) {
          throw new Error('Failed to fetch admin user')
        }
        const data = await response.json()
        setFormData({
          status: data.is_active ? 'active' : 'inactive',
          adminLevel: data.admin_level === 'super_admin' ? 'superadmin' : 'admin',
          name: data.name,
          email: data.email,
          password: '',
        })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred')
      } finally {
        setIsLoading(false)
      }
    }

    fetchAdminUser()
  }, [id, fetchWithAuth])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prevData => ({ ...prevData, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/admin/admin-users/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          admin_level: formData.adminLevel === 'superadmin' ? 'super_admin' : 'admin',
          is_active: formData.status === 'active' ? 1 : 0,
          ...(formData.password && { password: formData.password })
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to update admin user')
      }

      setMessage('Admin user successfully updated!')
      navigate('/admin-users')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>
  }

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900">
      <main className="container mx-auto px-4 py-8">
        <PageTitleBar
          title="Edit Admin User"
          breadcrumbs={[
            { label: 'Dashboard', href: '/' },
            { label: 'Admin Users', href: '/admin-users' },
            { label: 'Edit User' },
          ]}
        />

        <div className="bg-white rounded-sm border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <FormLabel htmlFor="status">STATUS</FormLabel>
              <FormSelect
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
              >
                <option value="">Select Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </FormSelect>
            </div>

            <div>
              <FormLabel htmlFor="adminLevel">ADMIN LEVEL</FormLabel>
              <FormSelect
                id="adminLevel"
                name="adminLevel"
                value={formData.adminLevel}
                onChange={handleChange}
                required
              >
                <option value="">Select Admin Level</option>
                <option value="superadmin">Super Admin</option>
                <option value="admin">Admin</option>
              </FormSelect>
            </div>

            <div>
              <FormLabel htmlFor="name">NAME</FormLabel>
              <FormInput
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <FormLabel htmlFor="email">EMAIL</FormLabel>
              <FormInput
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <FormLabel htmlFor="password">PASSWORD</FormLabel>
              <FormInput
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Leave blank to keep current password"
              />
              <p className="mt-1 text-sm text-gray-500">Minimum 5 characters with at least 1 letter and 1 number</p>
            </div>

            <div className="flex justify-end space-x-3">
              <CancelButton type="button" onClick={() => navigate('/admin-users')}>
                Cancel
              </CancelButton>
              <FormButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Updating...' : 'Update'}
              </FormButton>
            </div>
            {error && <div className="text-red-500 mt-2">{error}</div>}
          </form>
        </div>
      </main>
    </div>
  )
}
